import { sendApiRequest } from '@/services/apiRequests'

const baseUrl = () => process.env.VUE_APP_API_URL + 'file_storage/'

/** 5 MiB — shorter PUTs when the connection is unstable (see backend note) */
export const CHUNK_SIZE_BYTES = 5 * 1024 * 1024

const CHUNK_PUT_MAX_ATTEMPTS = 80

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const backoffMs = (attemptIndex) =>
    Math.min(500 * Math.pow(2, Math.min(attemptIndex, 10)), 10000)

const bearerHeaders = () => ({
    'Authorization': 'Bearer ' + sessionStorage.getItem('access_token')
})

const parseErrorMessage = async (response) => {
    const errBody = await response.json().catch(() => ({}))
    return errBody.detail || `Request failed: ${response.status}`
}

export const generateUploadId = () => {
    return crypto.randomUUID ? crypto.randomUUID() : (
        'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
        })
    )
}

export const initChunkedUpload = async ({ filename, description, total_size, total_parts, upload_id }) => {
    const body = {
        filename,
        total_size,
        total_parts
    }
    if (description) body.description = description
    if (upload_id) body.upload_id = upload_id

    const response = await fetch(baseUrl() + 'chunked/init', {
        method: 'POST',
        headers: {
            ...bearerHeaders(),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    })

    if (!response.ok) {
        throw new Error(await parseErrorMessage(response))
    }

    return await response.json().catch(() => ({}))
}

/**
 * PUT one chunk. On HTTP 408 or network failure, retries the same part_number and body
 * (does not restart the whole upload while the session is still valid on the server).
 */
export const putChunk = async (uploadId, partNumber, chunkBlob) => {
    const params = new URLSearchParams({
        upload_id: uploadId,
        part_number: String(partNumber)
    })

    const url = baseUrl() + 'chunked/chunk?' + params.toString()

    for (let attempt = 1; attempt <= CHUNK_PUT_MAX_ATTEMPTS; attempt++) {
        try {
            const response = await fetch(url, {
                method: 'PUT',
                headers: bearerHeaders(),
                body: chunkBlob
            })

            if (response.status === 408) {
                await sleep(backoffMs(attempt - 1))
                continue
            }

            if (!response.ok) {
                throw new Error(await parseErrorMessage(response))
            }

            return response
        } catch (err) {
            if (err?.name === 'AbortError') {
                throw err
            }

            const isNetwork =
                err instanceof TypeError ||
                (typeof err?.message === 'string' && err.message.includes('Failed to fetch'))

            if (isNetwork && attempt < CHUNK_PUT_MAX_ATTEMPTS) {
                await sleep(backoffMs(attempt - 1))
                continue
            }

            throw err
        }
    }

    throw new Error(`Chunk ${partNumber}: exceeded max retry attempts (${CHUNK_PUT_MAX_ATTEMPTS})`)
}

export const completeChunkedUpload = async (uploadId) => {
    const response = await fetch(baseUrl() + 'chunked/complete', {
        method: 'POST',
        headers: {
            ...bearerHeaders(),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ upload_id: uploadId })
    })

    if (!response.ok) {
        throw new Error(await parseErrorMessage(response))
    }

    return await response.json()
}

export const abortChunkedSession = async (uploadId) => {
    try {
        const response = await fetch(baseUrl() + 'chunked/session/' + encodeURIComponent(uploadId), {
            method: 'DELETE',
            headers: bearerHeaders()
        })
        if (!response.ok) {
            console.warn('abortChunkedSession:', response.status)
        }
    } catch (e) {
        console.warn('abortChunkedSession failed:', e)
    }
}

export const uploadFileChunked = async (file, description, uploadId, onPartProgress) => {
    const totalSize = file.size
    const totalParts = totalSize === 0 ? 1 : Math.ceil(totalSize / CHUNK_SIZE_BYTES)

    try {
        await initChunkedUpload({
            filename: file.name,
            description: description || undefined,
            total_size: totalSize,
            total_parts: totalParts,
            upload_id: uploadId
        })

        for (let part = 1; part <= totalParts; part++) {
            const start = (part - 1) * CHUNK_SIZE_BYTES
            const end = Math.min(part * CHUNK_SIZE_BYTES, totalSize)
            const chunk = file.slice(start, end)

            await putChunk(uploadId, part, chunk)

            if (typeof onPartProgress === 'function') {
                onPartProgress(part, totalParts)
            }
        }

        return await completeChunkedUpload(uploadId)
    } catch (err) {
        await abortChunkedSession(uploadId)
        throw err
    }
}

export const getUploadProgress = async (uploadId) => {
    const response = await fetch(baseUrl() + 'upload_progress/' + uploadId, {
        method: 'GET',
        headers: bearerHeaders()
    })

    if (response.status === 404) {
        return { data: { status: 'pending', progress: 0 } }
    }

    if (!response.ok) {
        throw new Error(`Progress check failed: ${response.status}`)
    }

    return { data: await response.json() }
}

export const getFileList = async (limit = 100, skip = 0) => {
    const config = {
        method: 'get',
        url: baseUrl(),
        params: { limit, skip }
    }

    return await sendApiRequest(config)
}

export const getFileMetadata = async (fileId) => {
    const config = {
        method: 'get',
        url: baseUrl() + fileId
    }

    return await sendApiRequest(config)
}

export const updateFileDescription = async (fileId, description) => {
    const config = {
        method: 'patch',
        url: baseUrl() + fileId,
        data: new URLSearchParams({ description }),
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        }
    }

    return await sendApiRequest(config)
}

export const deleteFile = async (fileId) => {
    const config = {
        method: 'delete',
        url: baseUrl() + fileId
    }

    return await sendApiRequest(config)
}
